import { useNavigationHandlers, useScrollHandler } from '@src/shared/hooks';
import { useModalControls } from './use-model-controls';
import { useEffect, useState } from 'react';
import {
  useProposalState,
  useSelectedLoanSimulationState,
  useGTM,
} from '@src/shared/hooks';
import { GTMEvents } from '@src/config/gtm';
import { setEtapa } from '@src/shared/zustand';
import { Etapa } from '@src/shared/models/etapa';

type ModalRedictProps = {
  kind: 'copy' | 'copyBarCode' | 'redirect' | 'download';
  open: boolean;
  openToast: boolean;
};

export const useTicketController = () => {
  const [modalRedict, setModalRedirect] = useState<ModalRedictProps>({
    kind: 'redirect',
    open: false,
    openToast: false,
  });
  const { proposal } = useProposalState();
  const { loanSimulation } = useSelectedLoanSimulationState();
  const navigationHandlers = useNavigationHandlers();
  const modalControls = useModalControls();
  const { isFixed } = useScrollHandler(modalControls.openModelMoreDetails);
  const { trackEvent } = useGTM();

  useEffect(() => {
    setEtapa(Etapa.ENTRY_TICKET_GENERATED);
    trackEvent(GTMEvents.ENTRY_TICKET_GENERATED, {
      proposal_id: proposal.proposalId,
      qtd_parcelas: loanSimulation?.schedule.length,
      valor_parcelas: loanSimulation?.schedule[0].payment,
      valor_total: loanSimulation?.totalAmountOwedWithDownPayment,
      valor_entrada: loanSimulation?.downPayment,
      valor_viagem: loanSimulation?.totalPriceOfFlightTicket,
    });
  }, []);

  const redirect = () => {
    setTimeout(() => {
      window.open(
        proposal.successfulRedirectUrl
          ? proposal.successfulRedirectUrl
          : 'https://www.voegol.com.br/',
        '_self'
      );
    }, 5000);
  };

  const onCountDownRedirect = () => {
    setModalRedirect({
      kind: 'redirect',
      open: true,
      openToast: false,
    });

    redirect();
  };

  const onDownloadTicket = () => {
    trackEvent(GTMEvents.DOWNLOAD_TICKET, {
      proposal_id: proposal.proposalId,
    });

    window.open(proposal.payments[0].link);
  };

  const onCopy = () => {
    navigator.clipboard
      .writeText(proposal.payments[0].pix.copyAndPaste)
      .finally(() => {
        if (modalRedict.openToast) {
          closeToast();
        }

        trackEvent(GTMEvents.COPY_PIX, {
          proposal_id: proposal.proposalId,
        });

        setTimeout(() => {
          setModalRedirect({
            kind: 'copy',
            open: false,
            openToast: true,
          });
        }, 200);
      });
  };

  const onCopyBarCode = () => {
    navigator.clipboard.writeText(proposal.payments[0].bankLine).finally(() => {
      if (modalRedict.openToast) {
        closeToast();
      }

      trackEvent(GTMEvents.COPY_TICKET, {
        proposal_id: proposal.proposalId,
      });

      setTimeout(() => {
        setModalRedirect({
          kind: 'copyBarCode',
          open: false,
          openToast: true,
        });
      }, 200);
    });
  };

  const closeToast = () => {
    setModalRedirect({
      ...modalRedict,
      openToast: false,
    });
  };

  const calculateSessionTTL = (): number => {
    const now = new Date();
    const { delayLimitInSeconds, start } = proposal?.sessionTime;
    const sessionEndTimestamp =
      new Date(start).getTime() + delayLimitInSeconds * 1000;

    const ttlInMilliseconds = sessionEndTimestamp - now.getTime();
    const ttlInSeconds = Math.floor(ttlInMilliseconds / 1000);

    return Math.max(0, ttlInSeconds);
  };

  return {
    ...navigationHandlers,
    ...modalControls,
    isFixed,
    closeToast,
    proposal,
    loanSimulation,
    validateForm: modalControls.terms && modalControls.privatePolicy,
    modalRedict,
    onCountDownRedirect,
    onCopy,
    onCopyBarCode,
    onDownloadTicket,
    calculateSessionTTL,
  };
};
